import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  ChevronLeft, ChevronRight, Plus, Calendar as CalendarIcon,
  AlertTriangle, Pencil, Trash2, X, List, LayoutGrid, Sun,
} from 'lucide-react';
import {
  Card, CardHeader, CardBody, Button, Badge, EmptyState,
  Modal, Label, Input, Select, Textarea, Alert,
  Skeleton,
} from '../components/ui';
import FirstVisitTips from '../components/onboarding/FirstVisitTips';
import { useFirm } from '../contexts/FirmContext';
import { useAuth } from '../contexts/AuthContext';
import { usePermissions } from '../contexts/PermissionsContext';
import {
  listEvents, listMatters, listDeadlines,
  createEvent, updateEvent, deleteEvent,
  createDeadline, updateDeadline, deleteDeadline,
  listFirmUsers, listEmployees,
} from '../lib/db';
import { EVENT_TYPES, DEADLINE_RULE_TEMPLATES } from '../lib/constants';
import { classNames, formatDate, formatRelative } from '../lib/formatters';
import { computeDeadlineChain, computeDeadline } from '../lib/deadlineCalculator';
import { withTimeout } from '../lib/fetchWithTimeout';
import toast from '../lib/toast';

// ─── Date helpers ─────────────────────────────────────────────────────────────
const startOfMonth = (d) => { const x = new Date(d); x.setDate(1); x.setHours(0,0,0,0); return x; };
const startOfWeek  = (d) => { const x = new Date(d); x.setDate(x.getDate() - x.getDay()); x.setHours(0,0,0,0); return x; };
const addDays      = (d, n) => { const x = new Date(d); x.setDate(x.getDate() + n); return x; };
const sameDay      = (a, b) => a.toDateString() === b.toDateString();
const toLocalDate  = (iso) => { const d = new Date(iso); return new Date(d.getFullYear(), d.getMonth(), d.getDate()); };
const fmtTime      = (iso) => iso ? new Date(iso).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : '';
const fmtDatetimeLocal = (iso) => iso ? new Date(iso).toISOString().slice(0, 16) : '';

// Build a datetime-local string for a given day + hour (for time-slot click-to-create)
const toSlotDatetime = (day, hour, minute = 0) => {
  const d = new Date(day);
  d.setHours(hour, minute, 0, 0);
  return d.toISOString().slice(0, 16);
};

// Returns true if `day` falls on or between the event's start and end dates
const eventSpansDay = (e, day) => {
  const s = toLocalDate(e.start_at);
  const en = e.end_at ? toLocalDate(e.end_at) : s;
  return day >= s && day <= en;
};
const isMultiDayEvent = (e) => e.end_at && toLocalDate(e.end_at) > toLocalDate(e.start_at);

// Week/Day view constants
const HOUR_START = 7;   // 7 am
const HOUR_END   = 20;  // 8 pm
const SLOT_PX    = 56;  // px per hour
const TOTAL_PX   = (HOUR_END - HOUR_START) * SLOT_PX;
const HOURS      = Array.from({ length: HOUR_END - HOUR_START }, (_, i) => HOUR_START + i);

const DEADLINE_STATUSES = [
  { value: 'open',      label: 'Open' },
  { value: 'completed', label: 'Completed' },
  { value: 'waived',    label: 'Waived' },
];

// ─── Event overlap layout ──────────────────────────────────────────────────────
// Given single-day timed events, assigns _col and _numCols so overlapping events
// render side-by-side. _numCols is per-event (its overlap group), NOT the day max,
// so non-overlapping events are never unnecessarily narrowed.
function computeEventColumns(dayEvents) {
  if (!dayEvents.length) return [];
  const sorted = [...dayEvents].sort((a, b) => new Date(a.start_at) - new Date(b.start_at));
  const getEnd = (e) => e.end_at
    ? new Date(e.end_at).getTime()
    : new Date(e.start_at).getTime() + 3600000;

  const colEnds = [];
  const placed = sorted.map((ev) => {
    const start = new Date(ev.start_at).getTime();
    let col = 0;
    while (col < colEnds.length && colEnds[col] > start) col++;
    colEnds[col] = getEnd(ev);
    return { ev, col };
  });

  // Each event's _numCols = size of ITS overlap group, not the whole-day max.
  return placed.map(({ ev, col }) => {
    const evStart = new Date(ev.start_at).getTime();
    const evEnd   = getEnd(ev);
    let maxCol = col;
    placed.forEach(({ ev: other, col: otherCol }) => {
      if (other === ev) return;
      const oStart = new Date(other.start_at).getTime();
      const oEnd   = getEnd(other);
      if (oStart < evEnd && oEnd > evStart) maxCol = Math.max(maxCol, otherCol);
    });
    return { ...ev, _col: col, _numCols: maxCol + 1 };
  });
}

// ─── All-day strip row layout ─────────────────────────────────────────────────
// Assigns each multi-day event and deadline a fixed row so items stay visually
// aligned across all columns (same approach Google Calendar uses).
// Returns: grid[rowIndex][dayIndex] = cell | null
function computeAllDayRows(weekDays, events, deadlines) {
  const mdEvs = events
    .filter((e) => isMultiDayEvent(e) && weekDays.some((d) => eventSpansDay(e, d)))
    .sort((a, b) => new Date(a.start_at) - new Date(b.start_at));

  const dls = deadlines
    .filter((d) => d.due_date && weekDays.some((day) => sameDay(toLocalDate(d.due_date), day)))
    .sort((a, b) => new Date(a.due_date) - new Date(b.due_date));

  const rowEnds = [];   // rowEnds[row] = latest end date in that row
  const itemRow = new Map();

  // Assign multi-day events first
  for (const ev of mdEvs) {
    const evStart = toLocalDate(ev.start_at);
    const evEnd   = ev.end_at ? toLocalDate(ev.end_at) : evStart;
    let row = 0;
    while (row < rowEnds.length && rowEnds[row] >= evStart) row++;
    rowEnds[row] = evEnd;
    itemRow.set(ev.id, row);
  }

  // Deadlines go after all event rows
  const dlRowBase = rowEnds.length;
  for (const dl of dls) {
    const dlDate = toLocalDate(dl.due_date);
    let row = dlRowBase;
    while (row < rowEnds.length && rowEnds[row] >= dlDate) row++;
    rowEnds[row] = dlDate;
    itemRow.set(dl.id, row);
  }

  const totalRows = rowEnds.length;
  // Build 2-D grid
  const grid = Array.from({ length: totalRows }, () => Array(weekDays.length).fill(null));

  for (const ev of mdEvs) {
    const row = itemRow.get(ev.id);
    const evStart = toLocalDate(ev.start_at);
    const evEnd   = ev.end_at ? toLocalDate(ev.end_at) : evStart;
    weekDays.forEach((day, di) => {
      if (day >= evStart && day <= evEnd) {
        grid[row][di] = {
          type:    sameDay(day, evStart) ? 'start' : 'continue',
          isEnd:   sameDay(day, evEnd),
          item:    ev,
        };
      }
    });
  }

  for (const dl of dls) {
    const row = itemRow.get(dl.id);
    const dlDate = toLocalDate(dl.due_date);
    weekDays.forEach((day, di) => {
      if (sameDay(day, dlDate)) grid[row][di] = { type: 'deadline', item: dl };
    });
  }

  return { grid, totalRows };
}

// ─── Shared time grid helpers ─────────────────────────────────────────────────
const evTop = (e) => {
  const d = new Date(e.start_at);
  return ((d.getHours() + d.getMinutes() / 60) - HOUR_START) * SLOT_PX;
};
const evHeight = (e) => {
  const start = new Date(e.start_at);
  const end   = e.end_at ? new Date(e.end_at) : new Date(start.getTime() + 3600000);
  return (Math.max((end - start) / 60000, 30) / 60) * SLOT_PX;
};

// Hook: current-time pixel position (updates every minute)
function useNowPx() {
  const [nowPx, setNowPx] = useState(null);
  useEffect(() => {
    const calc = () => {
      const now = new Date();
      const px = ((now.getHours() + now.getMinutes() / 60) - HOUR_START) * SLOT_PX;
      setNowPx(px >= 0 && px <= TOTAL_PX ? px : null);
    };
    calc();
    const id = setInterval(calc, 60000);
    return () => clearInterval(id);
  }, []);
  return nowPx;
}

// ─── Main page ────────────────────────────────────────────────────────────────
export default function CalendarPage() {
  const { firmId, refreshSignal } = useFirm();
  const { user } = useAuth();
  const currentUserId = user?.id;
  const { isAdmin } = usePermissions();
  const [view, setView]         = useState('month'); // 'month' | 'week' | 'day' | 'agenda'
  const [cursor, setCursor]     = useState(new Date());
  const [events, setEvents]     = useState([]);
  const [deadlines, setDeadlines] = useState([]);
  const [matters, setMatters]   = useState([]);
  const [firmUsers, setFirmUsers] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading]   = useState(true);

  // Modal state
  const [eventModal, setEventModal]       = useState(null); // null | 'new' | event-obj
  const [prefillDate, setPrefillDate]     = useState('');
  const [deadlineModal, setDeadlineModal] = useState(null);
  const [showChain, setShowChain]         = useState(false);

  // Compute fetch range from cursor + view
  const fetchRange = useMemo(() => {
    if (view === 'month') {
      const s = startOfMonth(cursor);
      const e = new Date(s); e.setMonth(e.getMonth() + 1); e.setDate(0);
      return { start: addDays(s, -7), end: addDays(e, 7) };
    }
    if (view === 'week') {
      const s = startOfWeek(cursor);
      return { start: s, end: addDays(s, 6) };
    }
    if (view === 'day') {
      return { start: addDays(cursor, -1), end: addDays(cursor, 1) };
    }
    // agenda: next 60 days
    return { start: new Date(), end: addDays(new Date(), 60) };
  }, [view, cursor]);

  useEffect(() => {
    if (!firmId) { setLoading(false); return; }
    setLoading(true);
    withTimeout(Promise.all([
      listEvents(firmId, fetchRange.start.toISOString(), fetchRange.end.toISOString()),
      listDeadlines(firmId, false),
      listMatters(firmId),
      listFirmUsers(firmId),
      listEmployees(firmId),
    ]), 20000).then(([ev, dl, m, fu, emp]) => {
      setEvents(ev.data || []);
      setDeadlines(dl.data || []);
      setMatters(m.data || []);
      setFirmUsers(fu.data || []);
      setEmployees(emp.data || []);
    }).catch(() => {})
    .finally(() => setLoading(false));
  }, [firmId, fetchRange, refreshSignal]);

  // Navigation
  const goBack = () => {
    if (view === 'week')       setCursor((c) => addDays(c, -7));
    else if (view === 'day')   setCursor((c) => addDays(c, -1));
    else                       setCursor((c) => { const d = new Date(c); d.setMonth(d.getMonth() - 1); return d; });
  };
  const goForward = () => {
    if (view === 'week')       setCursor((c) => addDays(c, 7));
    else if (view === 'day')   setCursor((c) => addDays(c, 1));
    else                       setCursor((c) => { const d = new Date(c); d.setMonth(d.getMonth() + 1); return d; });
  };
  const goToday = () => setCursor(new Date());

  const navLabel = useMemo(() => {
    if (view === 'week') {
      const ws = startOfWeek(cursor);
      const we = addDays(ws, 6);
      return `${ws.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} – ${we.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`;
    }
    if (view === 'day') {
      return cursor.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' });
    }
    if (view === 'agenda') return 'Upcoming — next 60 days';
    return cursor.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
  }, [view, cursor]);

  // ── Event handlers ──
  const openNewEvent = (dateStr = '') => {
    setPrefillDate(dateStr);
    setEventModal('new');
  };
  const openEditEvent  = (ev) => setEventModal(ev);
  const closeEventModal = () => { setEventModal(null); setPrefillDate(''); };

  const handleEventSaved = (saved, isEdit) => {
    if (isEdit) {
      setEvents((prev) => prev.map((e) => e.id === saved.id ? saved : e));
    } else {
      setEvents((prev) => [...prev, saved]);
    }
    closeEventModal();
  };
  const handleEventDeleted = (id) => {
    setEvents((prev) => prev.filter((e) => e.id !== id));
    closeEventModal();
  };

  const openEditDeadline   = (dl) => setDeadlineModal(dl);
  const closeDeadlineModal = () => setDeadlineModal(null);
  const handleDeadlineSaved = (saved) => {
    setDeadlines((prev) => prev.map((d) => d.id === saved.id ? { ...d, ...saved } : d));
    closeDeadlineModal();
  };
  const handleDeadlineDeleted = (id) => {
    setDeadlines((prev) => prev.filter((d) => d.id !== id));
    closeDeadlineModal();
  };

  // Drill down to day view from month grid
  const drillDownToDay = (dateStr) => {
    const [y, m, d] = dateStr.split('-').map(Number);
    setCursor(new Date(y, m - 1, d));
    setView('day');
  };

  return (
    <div className="space-y-6">
      {/* ── Header ── */}
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Calendar & Deadlines</h1>
          <p className="text-sm text-slate-500 dark:text-slate-400 mt-0.5">Track hearings, depositions, and court-rule-driven deadlines.</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button variant="secondary" icon={AlertTriangle} onClick={() => setShowChain(true)}><span className="hidden sm:inline">Compute deadline chain</span><span className="sm:hidden">Deadlines</span></Button>
          <Button icon={Plus} onClick={() => openNewEvent()}>New event</Button>
        </div>
      </div>

      <FirstVisitTips
        pageKey="calendar"
        title="Two things live here: events and deadlines"
        tips={[
          'Events are time-blocked — hearings, depositions, client meetings. Drag-create on the week and day views.',
          'Deadlines are date-only — court filing dates, statute of limitations. Use Compute deadline chain to auto-fill multi-step procedural deadlines from a court rule.',
          'Switch to Agenda for a chronological list of everything coming up in the next 60 days.',
        ]}
      />

      {/* ── View switcher + nav ── */}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        {/* View tabs */}
        <div className="flex rounded-lg border border-slate-200 dark:border-slate-700 overflow-hidden">
          {[
            { key: 'month',  label: 'Month',  icon: LayoutGrid },
            { key: 'week',   label: 'Week',   icon: CalendarIcon },
            { key: 'day',    label: 'Day',    icon: Sun },
            { key: 'agenda', label: 'Agenda', icon: List },
          ].map(({ key, label, icon: Icon }) => (
            <button
              key={key}
              onClick={() => setView(key)}
              className={classNames(
                'flex items-center gap-1.5 px-3 py-1.5 text-sm transition',
                view === key
                  ? 'bg-indigo-600 text-white'
                  : 'text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800',
              )}
            >
              <Icon size={13} /> <span className="hidden sm:inline">{label}</span>
            </button>
          ))}
        </div>

        {/* Navigation */}
        {view !== 'agenda' && (
          <div className="flex items-center gap-1">
            <Button variant="ghost" size="sm" icon={ChevronLeft} onClick={goBack} />
            <Button variant="ghost" size="sm" onClick={goToday}>Today</Button>
            <Button variant="ghost" size="sm" icon={ChevronRight} onClick={goForward} />
            <span className="text-sm font-medium text-slate-700 dark:text-slate-200 ml-2">{navLabel}</span>
          </div>
        )}
        {view === 'agenda' && (
          <span className="text-sm font-medium text-slate-500">{navLabel}</span>
        )}
      </div>

      {/* ── Calendar views ── */}
      {loading ? (
        <Card>
          <CardBody className="p-0">
            {/* Skeleton mimics the month-grid shape so the layout doesn't jump */}
            <div className="grid grid-cols-7 gap-px bg-slate-100 dark:bg-slate-800 p-px">
              {Array.from({ length: 42 }).map((_, i) => (
                <div key={i} className="aspect-square bg-white dark:bg-slate-900 p-2">
                  <Skeleton className="h-4 w-6 mb-2" />
                  <Skeleton className="h-2 w-full" />
                </div>
              ))}
            </div>
          </CardBody>
        </Card>
      ) : (
        <>
          {view === 'month' && (
            <MonthView
              cursor={cursor}
              events={events}
              deadlines={deadlines}
              onDayClick={(dateStr) => openNewEvent(dateStr)}
              onDayDrillDown={drillDownToDay}
              onEventClick={openEditEvent}
              onDeadlineClick={openEditDeadline}
            />
          )}
          {view === 'week' && (
            <WeekView
              cursor={cursor}
              events={events}
              deadlines={deadlines}
              onEventClick={openEditEvent}
              onDeadlineClick={openEditDeadline}
              onTimeSlotClick={(dt) => openNewEvent(dt)}
            />
          )}
          {view === 'day' && (
            <DayView
              cursor={cursor}
              events={events}
              deadlines={deadlines}
              onEventClick={openEditEvent}
              onDeadlineClick={openEditDeadline}
              onTimeSlotClick={(dt) => openNewEvent(dt)}
            />
          )}
          {view === 'agenda' && (
            <AgendaView
              events={events}
              deadlines={deadlines}
              onEventClick={openEditEvent}
              onDeadlineClick={openEditDeadline}
              onAddEvent={() => openNewEvent()}
            />
          )}
        </>
      )}

      {/* ── Upcoming deadlines panel (always shown below) ── */}
      <UpcomingDeadlines
        deadlines={deadlines}
        onEdit={openEditDeadline}
        onDelete={(dl) => {
          if (!window.confirm(`Delete "${dl.title}"?`)) return;
          deleteDeadline(dl.id).then(() => setDeadlines((prev) => prev.filter((d) => d.id !== dl.id)));
        }}
      />

      {/* ── Modals ── */}
      <EventModal
        open={!!eventModal}
        target={eventModal}
        prefillDate={prefillDate}
        matters={matters}
        firmId={firmId}
        currentUserId={currentUserId}
        isAdmin={isAdmin}
        firmUsers={firmUsers}
        employees={employees}
        onSaved={handleEventSaved}
        onDeleted={handleEventDeleted}
        onClose={closeEventModal}
      />

      <DeadlineEditModal
        open={!!deadlineModal}
        deadline={deadlineModal}
        matters={matters}
        firmId={firmId}
        onSaved={handleDeadlineSaved}
        onDeleted={handleDeadlineDeleted}
        onClose={closeDeadlineModal}
      />

      <DeadlineChainModal
        open={showChain}
        onClose={() => setShowChain(false)}
        matters={matters}
        firmId={firmId}
        onSaved={(newDl) => {
          setShowChain(false);
          if (newDl?.length) setDeadlines((prev) => [...prev, ...newDl]);
        }}
      />
    </div>
  );
}

// ─── Month view ───────────────────────────────────────────────────────────────
function MonthView({ cursor, events, deadlines, onDayClick, onDayDrillDown, onEventClick, onDeadlineClick }) {
  const grid = useMemo(() => {
    const first = startOfMonth(cursor);
    const startOffset = first.getDay();
    const firstCell = addDays(first, -startOffset);
    return Array.from({ length: 42 }, (_, i) => addDays(firstCell, i));
  }, [cursor]);

  const itemsForDay = (day) => ({
    ev: events.filter((e) => eventSpansDay(e, day)),
    dl: deadlines.filter((d) => d.due_date && sameDay(toLocalDate(d.due_date), day)),
  });

  return (
    <Card>
      <CardBody className="p-0">
        <div className="grid grid-cols-7 text-xs uppercase tracking-wide text-slate-500 dark:text-slate-400 border-b border-slate-100 dark:border-slate-800">
          {['Sun','Mon','Tue','Wed','Thu','Fri','Sat'].map((d) => (
            <div key={d} className="px-3 py-2 text-center font-medium">{d}</div>
          ))}
        </div>
        <div className="grid grid-cols-7">
          {grid.map((day, i) => {
            const outside = day.getMonth() !== cursor.getMonth();
            const today   = sameDay(day, new Date());
            const { ev, dl } = itemsForDay(day);
            const dateStr = `${day.getFullYear()}-${String(day.getMonth()+1).padStart(2,'0')}-${String(day.getDate()).padStart(2,'0')}`;
            const overflowCount = ev.length + dl.length - 4;
            return (
              <div
                key={i}
                onClick={() => onDayClick(dateStr)}
                className={classNames(
                  'relative min-h-[96px] border-b border-r border-slate-100 dark:border-slate-800 p-1.5 text-xs flex flex-col gap-1 cursor-pointer hover:bg-indigo-50/40 dark:hover:bg-indigo-900/10 transition',
                  outside && 'bg-slate-50/50 dark:bg-slate-900/40 text-slate-400',
                  today   && 'ring-2 ring-inset ring-indigo-500/60 bg-indigo-50/40 dark:bg-indigo-900/15',
                )}
              >
                <div className="flex items-center justify-between">
                  {/* Clicking the date number drills down to day view */}
                  <span
                    onClick={(x) => { x.stopPropagation(); onDayDrillDown(dateStr); }}
                    className={classNames(
                      'inline-flex items-center justify-center w-6 h-6 rounded-full text-xs cursor-pointer hover:ring-2 hover:ring-indigo-400 transition relative',
                      today ? 'bg-indigo-600 text-white font-bold' : 'font-medium',
                    )}
                    title="View day"
                  >
                    {day.getDate()}
                    {today && (
                      <span className="absolute -top-0.5 -right-0.5 flex h-2 w-2">
                        <span className="absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75 animate-ping" />
                        <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500" />
                      </span>
                    )}
                  </span>
                </div>
                {ev.slice(0, 2).map((e) => {
                  const t = EVENT_TYPES.find((x) => x.value === e.event_type) || EVENT_TYPES[0];
                  const multi   = isMultiDayEvent(e);
                  const isStart = sameDay(toLocalDate(e.start_at), day);
                  const isEnd   = e.end_at && sameDay(toLocalDate(e.end_at), day);

                  // For single-day timed events: bar position + width reflect the time of day
                  // Reference window: 6am–10pm = 16 hours (960 min) covers typical workday
                  const DAY_START_H = 6;
                  const DAY_SPAN_H  = 16;
                  let timeStyle = {};
                  if (!multi) {
                    const startH = new Date(e.start_at).getHours() + new Date(e.start_at).getMinutes() / 60;
                    const endH   = e.end_at
                      ? new Date(e.end_at).getHours() + new Date(e.end_at).getMinutes() / 60
                      : startH + 1;
                    const clampedStart = Math.max(startH - DAY_START_H, 0);
                    const clampedEnd   = Math.min(endH - DAY_START_H, DAY_SPAN_H);
                    const leftPct  = (clampedStart / DAY_SPAN_H) * 100;
                    const widthPct = Math.max(((clampedEnd - clampedStart) / DAY_SPAN_H) * 100, 14);
                    timeStyle = { marginLeft: `${leftPct}%`, width: `${Math.min(widthPct, 100 - leftPct)}%` };
                  }

                  return (
                    <div
                      key={e.id}
                      onClick={(x) => { x.stopPropagation(); onEventClick(e); }}
                      className={`py-0.5 text-white cursor-pointer hover:opacity-80 ${
                        multi
                          ? `px-1.5 ${isStart ? 'rounded-l ml-0' : '-ml-1.5 pl-0.5'} ${isEnd ? 'rounded-r mr-0' : '-mr-1.5 pr-0.5'}`
                          : 'truncate rounded px-1'
                      }`}
                      style={{ backgroundColor: t.color, ...timeStyle }}
                      title={`${e.title}${!multi ? ` · ${fmtTime(e.start_at)}${e.end_at ? '–' + fmtTime(e.end_at) : ''}` : ''}`}
                    >
                      {isStart ? (
                        <span className="truncate block">
                          {!multi && fmtTime(e.start_at) && <span className="opacity-75 mr-1">{fmtTime(e.start_at)}</span>}
                          {e.title}
                        </span>
                      ) : (
                        <span className="opacity-0 select-none text-[8px]">·</span>
                      )}
                    </div>
                  );
                })}
                {dl.slice(0, 2).map((d) => (
                  <div
                    key={d.id}
                    onClick={(x) => { x.stopPropagation(); onDeadlineClick(d); }}
                    className={classNames(
                      'truncate rounded px-1.5 py-0.5 border cursor-pointer hover:opacity-80',
                      d.status === 'completed'
                        ? 'bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-900/20 dark:text-emerald-300 dark:border-emerald-800'
                        : 'bg-red-50 text-red-700 border-red-200 dark:bg-red-900/20 dark:text-red-300 dark:border-red-800',
                    )}
                    title={d.title}
                  >
                    ⚠ {d.title}
                  </div>
                ))}
                {/* "+N more" drills down to day view */}
                {overflowCount > 0 && (
                  <button
                    onClick={(x) => { x.stopPropagation(); onDayDrillDown(dateStr); }}
                    className="text-[10px] text-indigo-500 hover:text-indigo-700 dark:text-indigo-400 hover:underline text-left"
                  >
                    +{overflowCount} more
                  </button>
                )}
              </div>
            );
          })}
        </div>
      </CardBody>
    </Card>
  );
}

// ─── Week all-day strip — row-aligned ────────────────────────────────────────
// Each multi-day event and deadline occupies a fixed row, aligned consistently
// across all day columns (like Google Calendar / Outlook).
const ROW_H = 22; // px per all-day row

function WeekAllDayStrip({ weekDays, events, deadlines, onEventClick, onDeadlineClick, colStyle }) {
  const { grid, totalRows } = useMemo(
    () => computeAllDayRows(weekDays, events, deadlines),
    [weekDays, events, deadlines],
  );
  if (totalRows === 0) return null;

  return (
    <div className="border-b border-slate-100 dark:border-slate-800">
      {grid.map((row, rowIdx) => (
        <div key={rowIdx} className="grid" style={{ gridTemplateColumns: colStyle }}>
          {/* Label on first row only */}
          <div
            className="border-r border-slate-100 dark:border-slate-800 text-[9px] text-slate-400 text-right pr-1.5 flex-shrink-0"
            style={{ height: `${ROW_H}px`, lineHeight: `${ROW_H}px` }}
          >
            {rowIdx === 0 ? 'all-day' : ''}
          </div>

          {weekDays.map((day, di) => {
            const cell = row[di];

            // Empty slot — keep the row height consistent
            if (!cell) {
              return (
                <div
                  key={di}
                  className="border-r border-slate-100 dark:border-slate-800 last:border-r-0"
                  style={{ height: `${ROW_H}px` }}
                />
              );
            }

            // Deadline chip — centered
            if (cell.type === 'deadline') {
              return (
                <div key={di} className="border-r border-slate-100 dark:border-slate-800 last:border-r-0 px-0.5 flex items-center" style={{ height: `${ROW_H}px` }}>
                  <div
                    onClick={() => onDeadlineClick(cell.item)}
                    className="text-[10px] truncate rounded px-1 py-0.5 w-full text-center bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300 cursor-pointer hover:opacity-80"
                    title={cell.item.title}
                  >
                    ⚠ {cell.item.title}
                  </div>
                </div>
              );
            }

            // Multi-day event bar — text centered within the bar
            const t = EVENT_TYPES.find((x) => x.value === cell.item.event_type) || EVENT_TYPES[0];
            const isStart = cell.type === 'start';
            const isEnd   = cell.isEnd;
            return (
              <div
                key={di}
                className={`border-r border-slate-100 dark:border-slate-800 last:border-r-0 flex items-center ${isStart ? 'pl-0.5' : ''} ${isEnd ? 'pr-0.5' : ''}`}
                style={{ height: `${ROW_H}px` }}
              >
                <div
                  onClick={() => onEventClick(cell.item)}
                  title={cell.item.title}
                  className={`h-[18px] flex items-center justify-center text-[10px] text-white cursor-pointer hover:opacity-80 w-full overflow-hidden ${
                    isStart ? 'rounded-l' : '-ml-px'
                  } ${isEnd ? 'rounded-r' : '-mr-px'}`}
                  style={{ backgroundColor: t.color }}
                >
                  {isStart && <span className="truncate px-2 text-center">{cell.item.title}</span>}
                </div>
              </div>
            );
          })}
        </div>
      ))}
    </div>
  );
}

// ─── Week view ────────────────────────────────────────────────────────────────
function WeekView({ cursor, events, deadlines, onEventClick, onDeadlineClick, onTimeSlotClick }) {
  const scrollRef = useRef(null);
  const nowPx = useNowPx();

  const weekDays = useMemo(() => {
    const ws = startOfWeek(cursor);
    return Array.from({ length: 7 }, (_, i) => addDays(ws, i));
  }, [cursor]);

  // Auto-scroll to current time on mount / view change
  useEffect(() => {
    if (scrollRef.current && nowPx !== null) {
      scrollRef.current.scrollTop = Math.max(0, nowPx - 120);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const timedForDay = (day) => events.filter((e) => !isMultiDayEvent(e) && sameDay(toLocalDate(e.start_at), day));

  const COL = '52px repeat(7, 1fr)';

  return (
    <Card>
      <CardBody className="p-0 overflow-x-auto">
        {/* Day headers */}
        <div className="grid border-b border-slate-100 dark:border-slate-800" style={{ gridTemplateColumns: COL }}>
          <div className="border-r border-slate-100 dark:border-slate-800" />
          {weekDays.map((day, i) => {
            const today = sameDay(day, new Date());
            return (
              <div key={i} className="px-2 py-3 text-center border-r border-slate-100 dark:border-slate-800 last:border-r-0">
                <p className="text-xs text-slate-500 uppercase tracking-wide">{day.toLocaleDateString('en-US', { weekday: 'short' })}</p>
                <span className={classNames(
                  'inline-flex items-center justify-center w-7 h-7 rounded-full text-sm font-semibold mt-0.5',
                  today ? 'bg-indigo-600 text-white' : 'text-slate-700 dark:text-slate-300',
                )}>
                  {day.getDate()}
                </span>
              </div>
            );
          })}
        </div>

        {/* All-day strip — row-aligned across all day columns */}
        <WeekAllDayStrip
          weekDays={weekDays}
          events={events}
          deadlines={deadlines}
          onEventClick={onEventClick}
          onDeadlineClick={onDeadlineClick}
          colStyle={COL}
        />

        {/* Time grid — scrollable */}
        <div ref={scrollRef} className="overflow-y-auto" style={{ maxHeight: '620px' }}>
          <div className="relative" style={{ height: `${TOTAL_PX}px` }}>
            <div className="grid h-full" style={{ gridTemplateColumns: COL }}>
              {/* Hour labels */}
              <div className="border-r border-slate-100 dark:border-slate-800 pointer-events-none">
                {HOURS.map((h) => (
                  <div
                    key={h}
                    className="border-b border-slate-100 dark:border-slate-800 text-right pr-2 text-[10px] text-slate-400"
                    style={{ height: `${SLOT_PX}px`, paddingTop: '2px' }}
                  >
                    {h % 12 || 12}{h < 12 ? 'am' : 'pm'}
                  </div>
                ))}
              </div>

              {/* Day columns */}
              {weekDays.map((day, di) => {
                const isToday = sameDay(day, new Date());
                const laid = computeEventColumns(timedForDay(day));
                return (
                  <div
                    key={di}
                    className={classNames(
                      'relative border-r border-slate-100 dark:border-slate-800 last:border-r-0',
                      isToday && 'bg-indigo-50/20 dark:bg-indigo-900/5',
                    )}
                  >
                    {/* Clickable hour slots */}
                    {HOURS.map((h) => (
                      <div
                        key={h}
                        className="border-b border-slate-100 dark:border-slate-800 cursor-pointer hover:bg-indigo-50/40 dark:hover:bg-indigo-900/10 transition-colors"
                        style={{ height: `${SLOT_PX}px` }}
                        onClick={() => onTimeSlotClick && onTimeSlotClick(toSlotDatetime(day, h))}
                      />
                    ))}

                    {/* Current time indicator — only on today */}
                    {isToday && nowPx !== null && (
                      <div
                        className="absolute left-0 right-0 flex items-center pointer-events-none z-20"
                        style={{ top: `${nowPx}px` }}
                      >
                        <div className="w-2 h-2 rounded-full bg-red-500 -ml-1 flex-shrink-0" />
                        <div className="flex-1 h-px bg-red-500" />
                      </div>
                    )}

                    {/* Events — side-by-side when overlapping */}
                    {laid.map((e) => {
                      const t = EVENT_TYPES.find((x) => x.value === e.event_type) || EVENT_TYPES[0];
                      const top = evTop(e);
                      const h   = evHeight(e);
                      if (top < 0 || top > TOTAL_PX) return null;
                      const colW = 1 / e._numCols;
                      return (
                        <div
                          key={e.id}
                          onClick={(x) => { x.stopPropagation(); onEventClick(e); }}
                          className="absolute rounded overflow-hidden text-white text-[10px] leading-tight px-1.5 py-1 cursor-pointer hover:opacity-90 transition shadow-sm z-10"
                          style={{
                            top:    `${top}px`,
                            height: `${Math.max(h, 20)}px`,
                            left:   `${e._col * colW * 100 + 0.5}%`,
                            width:  `${colW * 100 - 1}%`,
                            backgroundColor: t.color,
                          }}
                          title={e.title}
                        >
                          <p className="font-semibold truncate">{e.title}</p>
                          {h > 28 && <p className="opacity-80">{fmtTime(e.start_at)}</p>}
                        </div>
                      );
                    })}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </CardBody>
    </Card>
  );
}

// ─── Day view ─────────────────────────────────────────────────────────────────
function DayView({ cursor, events, deadlines, onEventClick, onDeadlineClick, onTimeSlotClick }) {
  const scrollRef = useRef(null);
  const nowPx     = useNowPx();
  const isToday   = sameDay(cursor, new Date());

  // Auto-scroll to current time
  useEffect(() => {
    if (scrollRef.current && nowPx !== null) {
      scrollRef.current.scrollTop = Math.max(0, nowPx - 120);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const timedEvents  = events.filter((e) => !isMultiDayEvent(e) && sameDay(toLocalDate(e.start_at), cursor));
  const multiEvents  = events.filter((e) => isMultiDayEvent(e) && eventSpansDay(e, cursor));
  const dayDeadlines = deadlines.filter((d) => d.due_date && sameDay(toLocalDate(d.due_date), cursor));
  const laid         = computeEventColumns(timedEvents);

  const hasAllDay = multiEvents.length > 0 || dayDeadlines.length > 0;

  return (
    <Card>
      <CardBody className="p-0">
        {/* Day header */}
        <div className="border-b border-slate-100 dark:border-slate-800 px-4 py-3 flex items-center gap-3">
          <div className={classNames(
            'w-11 h-11 rounded-xl flex flex-col items-center justify-center flex-shrink-0',
            isToday ? 'bg-indigo-600 text-white' : 'bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-200',
          )}>
            <span className="text-[10px] font-medium leading-none">
              {cursor.toLocaleDateString('en-US', { month: 'short' }).toUpperCase()}
            </span>
            <span className="text-xl font-bold leading-tight">{cursor.getDate()}</span>
          </div>
          <div>
            <p className="text-sm font-semibold text-slate-900 dark:text-white">
              {cursor.toLocaleDateString('en-US', { weekday: 'long' })}
              {isToday && <span className="ml-2 text-indigo-600 dark:text-indigo-400 font-normal">Today</span>}
            </p>
            <p className="text-xs text-slate-400">
              {timedEvents.length} event{timedEvents.length !== 1 ? 's' : ''}
              {dayDeadlines.length > 0 && ` · ${dayDeadlines.length} deadline${dayDeadlines.length !== 1 ? 's' : ''}`}
            </p>
          </div>
        </div>

        {/* All-day / multi-day strip — 52px gutter matches the hour-label column */}
        {hasAllDay && (
          <div className="border-b border-slate-100 dark:border-slate-800 flex">
            {/* 52px gutter — visually lines up with the hour labels below */}
            <div className="w-[52px] flex-shrink-0 border-r border-slate-100 dark:border-slate-800 text-[9px] text-slate-400 text-right pr-1.5 pt-2 leading-none">
              all-day
            </div>
            {/* Event bars — left edge aligns with time-grid event area */}
            <div className="flex-1 px-1 py-1.5 space-y-0.5 min-w-0">
              {multiEvents.map((e) => {
                const t = EVENT_TYPES.find((x) => x.value === e.event_type) || EVENT_TYPES[0];
                return (
                  <div
                    key={e.id}
                    onClick={() => onEventClick(e)}
                    className="text-xs py-1 rounded text-white cursor-pointer hover:opacity-80 truncate text-center"
                    style={{ backgroundColor: t.color }}
                  >
                    {e.title}
                  </div>
                );
              })}
              {dayDeadlines.map((d) => (
                <div
                  key={d.id}
                  onClick={() => onDeadlineClick(d)}
                  className="text-xs py-1 rounded bg-red-100 text-red-700 dark:bg-red-900/20 dark:text-red-300 cursor-pointer hover:opacity-80 truncate text-center"
                >
                  ⚠ {d.title}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Time grid — scrollable */}
        <div ref={scrollRef} className="overflow-y-auto" style={{ maxHeight: '620px' }}>
          <div className="relative flex" style={{ height: `${TOTAL_PX}px` }}>
            {/* Hour labels */}
            <div className="w-[52px] flex-shrink-0 border-r border-slate-100 dark:border-slate-800 pointer-events-none">
              {HOURS.map((h) => (
                <div
                  key={h}
                  className="border-b border-slate-100 dark:border-slate-800 text-right pr-2 text-[10px] text-slate-400"
                  style={{ height: `${SLOT_PX}px`, paddingTop: '2px' }}
                >
                  {h % 12 || 12}{h < 12 ? 'am' : 'pm'}
                </div>
              ))}
            </div>

            {/* Single day column */}
            <div className={classNames('flex-1 relative', isToday && 'bg-indigo-50/10 dark:bg-indigo-900/5')}>
              {/* Clickable hour slots */}
              {HOURS.map((h) => (
                <div
                  key={h}
                  className="border-b border-slate-100 dark:border-slate-800 cursor-pointer hover:bg-indigo-50/40 dark:hover:bg-indigo-900/10 transition-colors"
                  style={{ height: `${SLOT_PX}px` }}
                  onClick={() => onTimeSlotClick && onTimeSlotClick(toSlotDatetime(cursor, h))}
                />
              ))}

              {/* Current time indicator */}
              {isToday && nowPx !== null && (
                <div
                  className="absolute left-0 right-0 flex items-center pointer-events-none z-20"
                  style={{ top: `${nowPx}px` }}
                >
                  <div className="w-2 h-2 rounded-full bg-red-500 -ml-1 flex-shrink-0" />
                  <div className="flex-1 h-px bg-red-500" />
                </div>
              )}

              {/* Events — side-by-side when overlapping */}
              {laid.map((e) => {
                const t = EVENT_TYPES.find((x) => x.value === e.event_type) || EVENT_TYPES[0];
                const top = evTop(e);
                const h   = evHeight(e);
                if (top < 0 || top > TOTAL_PX) return null;
                const colW = 1 / e._numCols;
                return (
                  <div
                    key={e.id}
                    onClick={(x) => { x.stopPropagation(); onEventClick(e); }}
                    className="absolute rounded overflow-hidden text-white text-xs leading-tight px-2 py-1.5 cursor-pointer hover:opacity-90 transition shadow-sm z-10"
                    style={{
                      top:    `${top}px`,
                      height: `${Math.max(h, 22)}px`,
                      left:   `${e._col * colW * 100 + 0.5}%`,
                      width:  `${colW * 100 - 1}%`,
                      backgroundColor: t.color,
                    }}
                    title={e.title}
                  >
                    <p className="font-semibold truncate">{e.title}</p>
                    {h > 36 && (
                      <p className="opacity-80 text-[11px] mt-0.5">
                        {fmtTime(e.start_at)}
                        {e.end_at && ` – ${fmtTime(e.end_at)}`}
                        {e.location && ` · ${e.location}`}
                      </p>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </CardBody>
    </Card>
  );
}

// ─── Agenda view ──────────────────────────────────────────────────────────────
function AgendaView({ events, deadlines, onEventClick, onDeadlineClick, onAddEvent }) {
  const days = useMemo(() => {
    const today = new Date(); today.setHours(0,0,0,0);
    return Array.from({ length: 60 }, (_, i) => addDays(today, i));
  }, []);

  const itemsForDay = (day) => ({
    ev: events.filter((e) => eventSpansDay(e, day)),
    dl: deadlines.filter((d) => d.due_date && sameDay(toLocalDate(d.due_date), day)),
  });

  const activeDays = days.filter((d) => {
    const { ev, dl } = itemsForDay(d);
    return ev.length > 0 || dl.length > 0;
  });

  if (!activeDays.length) return (
    <Card><CardBody>
      <EmptyState
        icon={CalendarIcon}
        title="Your calendar is clear"
        description="No events or deadlines in the next 60 days. Add a court date, client meeting, or filing deadline so it shows up on your dashboard and reminds you when the day approaches."
        action={
          onAddEvent
            ? <Button icon={Plus} onClick={onAddEvent}>New event</Button>
            : null
        }
      />
    </CardBody></Card>
  );

  return (
    <Card>
      <CardBody className="p-0">
        <ul className="divide-y divide-slate-100 dark:divide-slate-800">
          {activeDays.map((day) => {
            const { ev, dl } = itemsForDay(day);
            const today = sameDay(day, new Date());
            return (
              <li key={day.toDateString()}>
                <div className={classNames(
                  'flex items-center gap-3 px-5 py-2.5 border-b border-slate-100 dark:border-slate-800',
                  today ? 'bg-indigo-50 dark:bg-indigo-900/20' : 'bg-slate-50/60 dark:bg-slate-800/30',
                )}>
                  <div className={classNames(
                    'w-10 h-10 rounded-xl flex flex-col items-center justify-center flex-shrink-0',
                    today ? 'bg-indigo-600 text-white' : 'bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-200',
                  )}>
                    <span className="text-[10px] font-medium leading-none">{day.toLocaleDateString('en-US', { month: 'short' }).toUpperCase()}</span>
                    <span className="text-lg font-bold leading-tight">{day.getDate()}</span>
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-slate-800 dark:text-slate-200">
                      {today ? 'Today · ' : ''}{day.toLocaleDateString('en-US', { weekday: 'long' })}
                    </p>
                    <p className="text-xs text-slate-400">{ev.length} event{ev.length !== 1 ? 's' : ''} · {dl.length} deadline{dl.length !== 1 ? 's' : ''}</p>
                  </div>
                </div>
                <ul className="px-5 py-2 space-y-1.5">
                  {ev.map((e) => {
                    const t = EVENT_TYPES.find((x) => x.value === e.event_type) || EVENT_TYPES[0];
                    const multi = isMultiDayEvent(e);
                    const totalDays = multi
                      ? Math.round((toLocalDate(e.end_at) - toLocalDate(e.start_at)) / 86400000) + 1
                      : 0;
                    const isStart = sameDay(toLocalDate(e.start_at), day);
                    return (
                      <li key={e.id} onClick={() => onEventClick(e)} className="flex items-center gap-3 py-1.5 cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-800/50 rounded-lg px-2 transition group">
                        <div className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: t.color }} />
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-slate-800 dark:text-slate-200 truncate">{e.title}</p>
                          <p className="text-xs text-slate-400">
                            {multi
                              ? isStart
                                ? `Starts today · ${totalDays} days${e.location ? ` · ${e.location}` : ''}`
                                : `Continues${e.location ? ` · ${e.location}` : ''}`
                              : `${fmtTime(e.start_at)}${e.end_at ? ` – ${fmtTime(e.end_at)}` : ''}${e.location ? ` · ${e.location}` : ''}`
                            }
                          </p>
                        </div>
                        <Pencil size={12} className="text-slate-300 opacity-0 group-hover:opacity-100 transition-opacity" />
                      </li>
                    );
                  })}
                  {dl.map((d) => (
                    <li key={d.id} onClick={() => onDeadlineClick(d)} className="flex items-center gap-3 py-1.5 cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-800/50 rounded-lg px-2 transition group">
                      <div className={classNames('w-2.5 h-2.5 rounded-full flex-shrink-0', d.status === 'completed' ? 'bg-emerald-500' : 'bg-red-500')} />
                      <div className="flex-1 min-w-0">
                        <p className={classNames('text-sm font-medium truncate', d.status === 'completed' ? 'text-slate-400 line-through' : 'text-slate-800 dark:text-slate-200')}>{d.title}</p>
                        <p className="text-xs text-slate-400">Deadline · {d.status}</p>
                      </div>
                      <Pencil size={12} className="text-slate-300 opacity-0 group-hover:opacity-100 transition-opacity" />
                    </li>
                  ))}
                </ul>
              </li>
            );
          })}
        </ul>
      </CardBody>
    </Card>
  );
}

// ─── Upcoming deadlines panel ─────────────────────────────────────────────────
function UpcomingDeadlines({ deadlines, onEdit, onDelete }) {
  const upcoming = deadlines
    .filter((d) => d.status === 'open' && d.due_date && new Date(d.due_date) <= addDays(new Date(), 30))
    .sort((a, b) => new Date(a.due_date) - new Date(b.due_date))
    .slice(0, 12);

  return (
    <Card>
      <CardHeader title="Upcoming deadlines (next 30 days)" />
      <CardBody className="p-0">
        {!upcoming.length ? (
          <EmptyState
            icon={CalendarIcon}
            title="No upcoming deadlines"
            description="Deadlines are date-driven obligations — court appearances, filing deadlines, statute of limitations. Add them per matter (Matter → Deadlines tab), or use a deadline rule template to auto-generate the full chain for a litigation matter."
          />
        ) : (
          <ul className="divide-y divide-slate-100 dark:divide-slate-800">
            {upcoming.map((d) => {
              const days = Math.ceil((new Date(d.due_date).getTime() - Date.now()) / 86400000);
              const tone = days < 0 ? 'red' : days < 3 ? 'red' : days < 7 ? 'amber' : 'slate';
              return (
                <li key={d.id} className="flex items-center gap-4 px-6 py-3 text-sm hover:bg-slate-50 dark:hover:bg-slate-800/40 group">
                  <Badge tone={tone}>{days < 0 ? `${Math.abs(days)}d overdue` : `${days}d`}</Badge>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium truncate">{d.title}</p>
                    <p className="text-xs text-slate-500">{formatDate(d.due_date)} · {d.matter?.matter_number || '—'}</p>
                  </div>
                  <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                    <button onClick={() => onEdit(d)} className="p-1.5 rounded hover:bg-slate-100 dark:hover:bg-slate-700 text-slate-400 hover:text-indigo-600" title="Edit"><Pencil size={13} /></button>
                    <button onClick={() => onDelete(d)} className="p-1.5 rounded hover:bg-red-50 dark:hover:bg-red-900/20 text-slate-400 hover:text-red-600" title="Delete"><Trash2 size={13} /></button>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </CardBody>
    </Card>
  );
}

// ─── Event modal (create / edit / delete) ─────────────────────────────────────
const BLANK_EVENT = {
  title: '', description: '', event_type: 'appointment',
  start_at: '', end_at: '', matter_id: '', location: '', attendee_user_ids: [],
};

function EventModal({ open, target, prefillDate, matters, firmId, currentUserId, isAdmin, firmUsers, employees, onSaved, onDeleted, onClose }) {
  const isEdit = target && target !== 'new';

  // Ownership: creator can edit; null created_by = legacy row (allow edit)
  const isOwner = !isEdit || !target?.created_by || target.created_by === currentUserId || isAdmin;

  const [form, setForm] = useState(BLANK_EVENT);
  const [saving, setSaving]   = useState(false);
  const [deleting, setDeleting] = useState(false);

  // Build combined colleague list
  const colleagues = useMemo(() => {
    const userList = (firmUsers || []).map((u) => ({
      id: u.id,
      name: [u.first_name, u.last_name].filter(Boolean).join(' ') || u.email || 'User',
      role: u.role || 'user',
    }));
    const empList = (employees || []).map((e) => ({
      id: e.id,
      name: [e.first_name, e.last_name].filter(Boolean).join(' ') || e.email || 'Employee',
      role: e.job_title || 'Staff',
    }));
    const seen = new Set(userList.map((u) => u.id));
    return [...userList, ...empList.filter((e) => !seen.has(e.id))];
  }, [firmUsers, employees]);

  useEffect(() => {
    if (!open) return;
    if (isEdit) {
      setForm({
        title:             target.title             || '',
        description:       target.description       || '',
        event_type:        target.event_type        || 'appointment',
        start_at:          fmtDatetimeLocal(target.start_at),
        end_at:            fmtDatetimeLocal(target.end_at),
        matter_id:         target.matter_id         || '',
        location:          target.location          || '',
        attendee_user_ids: target.attendee_user_ids || [],
      });
    } else {
      // prefillDate can be "YYYY-MM-DD" or "YYYY-MM-DDTHH:mm" (from time slot click)
      let base = '', end = '';
      if (prefillDate) {
        if (prefillDate.includes('T')) {
          // Time slot click — use as-is and set end 1 hour later
          base = prefillDate;
          const endDt = new Date(prefillDate);
          endDt.setHours(endDt.getHours() + 1);
          end = endDt.toISOString().slice(0, 16);
        } else {
          base = `${prefillDate}T09:00`;
          end  = `${prefillDate}T10:00`;
        }
      }
      setForm({ ...BLANK_EVENT, start_at: base, end_at: end });
    }
    // `isEdit` is derived from `target` (which IS in the deps) so we
    // don't need it as a separate dependency — including it would just
    // trigger duplicate re-runs when target changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, target, prefillDate]);

  const toggleAttendee = (id) => {
    setForm((f) => {
      const ids = f.attendee_user_ids || [];
      return {
        ...f,
        attendee_user_ids: ids.includes(id) ? ids.filter((x) => x !== id) : [...ids, id],
      };
    });
  };

  const submit = async () => {
    if (!form.title.trim()) { toast.warning('Title is required'); return; }
    setSaving(true);
    const payload = {
      ...form,
      start_at:          form.start_at ? new Date(form.start_at).toISOString() : new Date().toISOString(),
      end_at:            form.end_at ? new Date(form.end_at).toISOString() : null,
      matter_id:         form.matter_id || null,
      attendee_user_ids: form.attendee_user_ids || [],
    };
    if (isEdit) {
      const { data, error } = await updateEvent(target.id, payload);
      setSaving(false);
      if (error) { toast.error(error.message); return; }
      onSaved(data, true);
    } else {
      const { data, error } = await createEvent(firmId, payload);
      setSaving(false);
      if (error) { toast.error(error.message); return; }
      onSaved(data, false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm(`Delete "${target.title}"?`)) return;
    setDeleting(true);
    await deleteEvent(target.id);
    setDeleting(false);
    onDeleted(target.id);
  };

  const attendeeIds = form.attendee_user_ids || [];

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={
        <span className="flex items-center gap-2">
          {isEdit ? (isOwner ? 'Edit event' : 'View event') : 'New calendar event'}
          {isEdit && !isOwner && (
            <span className="inline-flex items-center gap-1 text-xs font-normal px-2 py-0.5 rounded-full bg-slate-100 dark:bg-slate-700 text-slate-500 dark:text-slate-400">
              <X size={10} /> Read only
            </span>
          )}
        </span>
      }
      size="md"
      footer={
        <div className="flex items-center justify-between w-full">
          {isEdit && isOwner ? (
            <Button variant="danger" loading={deleting} onClick={handleDelete}>Delete</Button>
          ) : <span />}
          <div className="flex gap-2">
            <Button variant="ghost" onClick={onClose}>{isOwner ? 'Cancel' : 'Close'}</Button>
            {isOwner && (
              <Button loading={saving} onClick={submit}>{isEdit ? 'Save changes' : 'Create event'}</Button>
            )}
          </div>
        </div>
      }
    >
      {/* Non-owner notice */}
      {isEdit && !isOwner && (
        <div className="mb-3 flex items-center gap-2 text-xs text-slate-500 dark:text-slate-400 bg-slate-50 dark:bg-slate-800/60 border border-slate-200 dark:border-slate-700 rounded-lg px-3 py-2">
          <X size={13} className="flex-shrink-0 text-slate-400" />
          Only the person who created this event can edit or delete it.
        </div>
      )}

      <div className="space-y-3">
        <div>
          <Label required>Title</Label>
          <Input
            value={form.title}
            onChange={(e) => isOwner && setForm({ ...form, title: e.target.value })}
            placeholder="e.g. Hearing on motion to dismiss"
            readOnly={!isOwner}
            className={!isOwner ? 'opacity-60 cursor-default' : ''}
          />
        </div>
        <div>
          <Label>Type</Label>
          <Select
            options={EVENT_TYPES}
            value={form.event_type}
            onChange={(e) => isOwner && setForm({ ...form, event_type: e.target.value })}
            disabled={!isOwner}
          />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label required>Start</Label>
            <Input
              type="datetime-local"
              value={form.start_at}
              onChange={(e) => isOwner && setForm({ ...form, start_at: e.target.value })}
              readOnly={!isOwner}
              className={!isOwner ? 'opacity-60 cursor-default' : ''}
            />
          </div>
          <div>
            <Label>End</Label>
            <Input
              type="datetime-local"
              value={form.end_at}
              onChange={(e) => isOwner && setForm({ ...form, end_at: e.target.value })}
              readOnly={!isOwner}
              className={!isOwner ? 'opacity-60 cursor-default' : ''}
            />
          </div>
        </div>
        <div>
          <Label>Case file</Label>
          <Select
            placeholder="None"
            options={[{ value: '', label: 'None' }, ...matters.map((m) => ({ value: m.id, label: `${m.matter_number} — ${m.title}` }))]}
            value={form.matter_id}
            onChange={(e) => isOwner && setForm({ ...form, matter_id: e.target.value })}
            disabled={!isOwner}
          />
        </div>
        <div>
          <Label>Location</Label>
          <Input
            value={form.location}
            onChange={(e) => setForm({ ...form, location: e.target.value })}
            placeholder="e.g. High Court, Room 4"
            readOnly={!isOwner}
            className={!isOwner ? 'opacity-60 cursor-default' : ''}
          />
        </div>
        <div>
          <Label>Description</Label>
          <Textarea
            rows={2}
            value={form.description}
            onChange={(e) => setForm({ ...form, description: e.target.value })}
            readOnly={!isOwner}
            className={!isOwner ? 'opacity-60 cursor-default' : ''}
          />
        </div>

        {/* Attendees */}
        {colleagues.length > 0 && (
          <div>
            <Label>
              Colleagues
              {attendeeIds.length > 0 && (
                <span className="ml-2 text-xs font-normal text-indigo-600 dark:text-indigo-400">
                  {attendeeIds.length} invited
                </span>
              )}
            </Label>
            <div className={`mt-1.5 rounded-xl border border-slate-200 dark:border-slate-700 overflow-hidden divide-y divide-slate-100 dark:divide-slate-800 max-h-48 overflow-y-auto${!isOwner ? ' opacity-60' : ''}`}>
              {colleagues.map((c) => {
                const checked = attendeeIds.includes(c.id);
                return (
                  <label
                    key={c.id}
                    className={`flex items-center gap-3 px-3 py-2.5 transition-colors ${
                      isOwner ? 'cursor-pointer' : 'cursor-default'
                    } ${
                      checked ? 'bg-indigo-50 dark:bg-indigo-900/20' : isOwner ? 'hover:bg-slate-50 dark:hover:bg-slate-800/40' : ''
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => isOwner && toggleAttendee(c.id)}
                      disabled={!isOwner}
                      className="rounded border-slate-300 text-indigo-600 focus:ring-indigo-500"
                    />
                    <div className="flex-1 min-w-0">
                      <p className={`text-sm font-medium truncate ${checked ? 'text-indigo-700 dark:text-indigo-300' : 'text-slate-700 dark:text-slate-200'}`}>
                        {c.name}
                      </p>
                      <p className="text-xs text-slate-400 truncate capitalize">{c.role}</p>
                    </div>
                    {checked && <span className="text-[10px] font-semibold text-indigo-600 dark:text-indigo-400 shrink-0">✓ Invited</span>}
                  </label>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </Modal>
  );
}

// ─── Deadline edit modal ──────────────────────────────────────────────────────
function DeadlineEditModal({ open, deadline, matters, firmId, onSaved, onDeleted, onClose }) {
  const [form, setForm] = useState({ title: '', due_date: '', status: 'open', rule_template: '', notes: '' });
  const [saving, setSaving]   = useState(false);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    if (open && deadline) {
      setForm({
        title:         deadline.title         || '',
        due_date:      deadline.due_date       || '',
        status:        deadline.status         || 'open',
        rule_template: deadline.rule_template  || '',
        notes:         deadline.notes          || '',
      });
    }
  }, [open, deadline]);

  const handleSave = async () => {
    if (!form.title.trim()) return;
    setSaving(true);
    const { data, error } = await updateDeadline(deadline.id, form);
    setSaving(false);
    if (error) { toast.error(error.message); return; }
    onSaved(data);
  };

  const handleDelete = async () => {
    if (!window.confirm(`Delete deadline "${deadline?.title}"?`)) return;
    setDeleting(true);
    await deleteDeadline(deadline.id);
    setDeleting(false);
    onDeleted(deadline.id);
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Edit deadline"
      footer={
        <div className="flex items-center justify-between w-full">
          <Button variant="danger" loading={deleting} onClick={handleDelete}>Delete</Button>
          <div className="flex gap-2">
            <Button variant="ghost" onClick={onClose}>Cancel</Button>
            <Button loading={saving} disabled={!form.title.trim()} onClick={handleSave}>Save changes</Button>
          </div>
        </div>
      }
    >
      <div className="space-y-4">
        <div>
          <Label>Title *</Label>
          <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label>Due date</Label>
            <Input type="date" value={form.due_date} onChange={(e) => setForm({ ...form, due_date: e.target.value })} />
          </div>
          <div>
            <Label>Status</Label>
            <Select value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })} options={DEADLINE_STATUSES} />
          </div>
        </div>
        <div>
          <Label>Rule / template <span className="text-slate-400 font-normal">(optional)</span></Label>
          <Input value={form.rule_template} onChange={(e) => setForm({ ...form, rule_template: e.target.value })} />
        </div>
        <div>
          <Label>Notes <span className="text-slate-400 font-normal">(optional)</span></Label>
          <Textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
        </div>
      </div>
    </Modal>
  );
}

// ─── Deadline chain modal ─────────────────────────────────────────────────────
function DeadlineChainModal({ open, onClose, matters, firmId, onSaved }) {
  const [matterId, setMatterId]       = useState('');
  const [triggerDate, setTriggerDate] = useState('');
  const [selectedRules, setSelectedRules] = useState(new Set());
  const [saving, setSaving] = useState(false);

  const preview = useMemo(() => {
    if (!triggerDate) return [];
    const rules = DEADLINE_RULE_TEMPLATES.filter((r) => selectedRules.has(r.value));
    return computeDeadlineChain(triggerDate, rules);
  }, [triggerDate, selectedRules]);

  const toggleRule = (v) => setSelectedRules((prev) => {
    const n = new Set(prev); if (n.has(v)) n.delete(v); else n.add(v); return n;
  });

  const save = async () => {
    if (!matterId || !preview.length) return;
    setSaving(true);
    const items = preview.filter((p) => p.dueDate);
    const results = await Promise.all(
      items.map((p) => createDeadline(firmId, {
        matter_id:            matterId,
        title:                p.rule.label,
        rule_template:        p.rule.value,
        jurisdiction:         p.rule.jurisdiction,
        due_date:             p.dueDate.toISOString().slice(0, 10),
        offset_days:          p.rule.offsetDays,
        offset_business_days: p.rule.businessDays,
      }))
    );
    setSaving(false);
    const firstError = results.find((r) => r.error);
    if (firstError) { toast.error(firstError.error.message); return; }
    const created = results.map((r) => r.data).filter(Boolean);
    setMatterId(''); setTriggerDate(''); setSelectedRules(new Set());
    onSaved(created);
  };

  return (
    <Modal open={open} onClose={onClose} title="Compute deadline chain" size="lg"
      footer={<><Button variant="ghost" onClick={onClose}>Cancel</Button><Button loading={saving} disabled={!matterId || !preview.length} onClick={save}>Save {preview.length} deadline{preview.length === 1 ? '' : 's'}</Button></>}>
      <div className="space-y-4">
        <Alert tone="info" title="How it works">
          Pick a matter and a trigger date (e.g. service date). Select applicable rules and the system computes cascading deadlines.
          <br />
          <span className="text-xs opacity-80">This is a simplified engine. For jurisdiction-specific rules, customize <code>src/lib/constants.js → DEADLINE_RULE_TEMPLATES</code>.</span>
        </Alert>
        <div className="grid grid-cols-2 gap-3">
          <div><Label required>Matter</Label>
            <Select placeholder="Select matter…" options={matters.map((m) => ({ value: m.id, label: `${m.matter_number} — ${m.title}` }))}
              value={matterId} onChange={(e) => setMatterId(e.target.value)} />
          </div>
          <div><Label required>Trigger date</Label>
            <Input type="date" value={triggerDate} onChange={(e) => setTriggerDate(e.target.value)} />
          </div>
        </div>
        <div>
          <Label>Rules to apply</Label>
          <div className="grid grid-cols-1 gap-2 mt-2">
            {DEADLINE_RULE_TEMPLATES.map((r) => (
              <label key={r.value} className="flex items-center gap-3 p-2 rounded border border-slate-200 dark:border-slate-800 cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-800">
                <input type="checkbox" checked={selectedRules.has(r.value)} onChange={() => toggleRule(r.value)} />
                <div className="flex-1 text-sm">
                  <p className="font-medium">{r.label}</p>
                  <p className="text-xs text-slate-500">{r.jurisdiction} · {r.offsetDays >= 0 ? '+' : ''}{r.offsetDays} {r.businessDays ? 'business' : 'calendar'} days</p>
                </div>
                {triggerDate && selectedRules.has(r.value) && (
                  <Badge tone="indigo">{formatDate(computeDeadline({ triggerDate, offsetDays: r.offsetDays, businessDays: r.businessDays }))}</Badge>
                )}
              </label>
            ))}
          </div>
        </div>
        {preview.length > 0 && (
          <div>
            <p className="text-xs uppercase tracking-wide text-slate-500 mb-2">Preview</p>
            <ul className="divide-y divide-slate-100 dark:divide-slate-800 border border-slate-200 dark:border-slate-800 rounded-lg">
              {preview.map((p, i) => (
                <li key={i} className="flex items-center justify-between px-3 py-2 text-sm">
                  <span>{p.rule.label}</span>
                  <span className="font-medium">{p.dueDate ? formatDate(p.dueDate) : '—'} <span className="text-xs text-slate-500">({formatRelative(p.dueDate)})</span></span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </Modal>
  );
}
